import React, { useCallback, useMemo, useSyncExternalStore } from 'react';
import { getAllSegments } from '@goldcast/player-config/src/utils/clipUtils';
import { approxEquals } from '@goldcast/player-config/src/utils/compareUtils';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import PlayerTimelineButton from './PlayerTimelineButton';
import { getAbsoluteTimeFromSegmentTime, getPlayerTimeFromSegmentTime } from '../timeUtils';
import { CLIP_UPDATE_TYPE } from '../clipUpdateDataUtils/constants';
import { updateClipData } from '../clipUpdateDataUtils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { defaultTimelineSelectionState, transcriptTimelineSelectionStore } from '@/stores/transcriptTimelineSelection';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';

export default function RestoreSelected({ isDisabled = false }: { isDisabled?: boolean }) {
  const { startTime, endTime, isSelecting, startSegmentId, endSegmentId } = useSyncExternalStore(
    transcriptTimelineSelectionStore.subscribe,
    transcriptTimelineSelectionStore.getSnapshot
  );

  const { clipData, clipId } = useClipsContext();
  const { seekTo } = useRemotionPlayerSeek();

  const shouldShowRestoreButton = useMemo(() => {
    if (!isSelecting && startTime !== null && endTime !== null && startSegmentId !== null && endSegmentId !== null) {
      const allSegments = getAllSegments(clipData);
      const startSegment = allSegments.find(segment => segment.id === startSegmentId);
      if (!startSegment) {
        return false;
      }
      const endSegment = allSegments.find(segment => segment.id === endSegmentId);
      if (!endSegment) {
        return false;
      }
      if (startSegment.type !== SegmentType.COMPOSITE || endSegment.type !== SegmentType.COMPOSITE) {
        return false;
      }
      if (startTime === endTime && startSegmentId === endSegmentId) {
        return false;
      }
      const playerStartTime = getPlayerTimeFromSegmentTime(startTime, clipData, startSegmentId);
      const absoluteStartTime = getAbsoluteTimeFromSegmentTime(startTime, clipData, startSegmentId, -Infinity);
      const playerEndTime = getPlayerTimeFromSegmentTime(endTime, clipData, endSegmentId);
      const absoluteEndTime = getAbsoluteTimeFromSegmentTime(endTime, clipData, endSegmentId, -Infinity);

      return !approxEquals(playerEndTime - playerStartTime, absoluteEndTime - absoluteStartTime);
    }
    return false;
  }, [isSelecting, startTime, endTime, startSegmentId, endSegmentId, clipData]);

  const clickHandler = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>) => {
      e.stopPropagation();
      e.preventDefault();

      if (startTime === null || endTime === null) {
        return;
      }

      const newClipData = updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.RESTORE_COMPOSITE_SEGMENT_DELETE,
        startTime,
        endTime,
        startCompositeId: startSegmentId || '',
        endCompositeId: endSegmentId || ''
      });

      transcriptTimelineSelectionStore.set(() => ({
        ...defaultTimelineSelectionState
      }));

      if (startSegmentId) {
        seekTo(getPlayerTimeFromSegmentTime(startTime, newClipData, startSegmentId));
      }
    },
    [clipId, endSegmentId, endTime, seekTo, startSegmentId, startTime]
  );

  if (!shouldShowRestoreButton) {
    return null;
  }

  return (
    <PlayerTimelineButton
      iconName="IconRotate"
      onClick={clickHandler}
      tooltipTitle="Restore"
      buttonId="restore-selected"
      isDisabled={isDisabled}
    />
  );
}
